import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MainLayout from './layout/MainLayout';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import WcIcon from '@mui/icons-material/Wc';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { brand } from '../theme';
import { FESTIVAL_HOURS_LABEL } from '../utils/festivalDates';

const primaryTextProps = {
  fontWeight: 600,
  fontSize: '1.125rem',
  color: brand.navy,
};

const Visitors = () => {
  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 } }}>
          <Typography variant="h1" sx={{ color: brand.navy, mb: 1.5 }}>
            For Visitors
          </Typography>
          <Typography
            sx={{
              color: brand.textSecondary,
              fontSize: '1.1875rem',
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            The Lucedale Fine Arts Club welcomes you to our annual arts and
            crafts festival. Here's everything you need to make the most of
            your visit.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.cream,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 1 }}>
            Welcome to Gingham Tree 2026
          </Typography>
          <Typography sx={{ fontSize: '1.0625rem', color: brand.textPrimary }}>
            Spend the day ({FESTIVAL_HOURS_LABEL}) discovering one-of-a-kind
            handmade goods from local artisans, enjoy great food and refreshments,
            and bring the whole family for a community tradition.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            What to know
          </Typography>
          <List sx={{ p: 0 }}>
            <Row
              icon={<EventIcon />}
              primary="Event Details"
              secondary={`Gingham Tree is always held the second Saturday in November, ${FESTIVAL_HOURS_LABEL}. Join us for a day of unique, handmade crafts and local art.`}
            />
            <Row
              icon={<RestaurantIcon />}
              primary="Food & Refreshments"
              secondary="A variety of food items and drinks can be purchased from the many food vendors at the festival."
            />
            <Row
              icon={<WcIcon />}
              primary="Restroom Locations"
              secondaryNode={
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: '1rem', color: brand.textSecondary }}>
                    Restroom facilities are located at:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2.5, color: brand.textPrimary }}>
                    <li>Outside of the Jaycees Building</li>
                    <li>Inside of the Jaycees Building</li>
                    <li>Arena's smaller concession stand</li>
                    <li>Arena's larger concession stand</li>
                  </Box>
                </Box>
              }
            />
            <Row
              icon={<ChildCareIcon />}
              primary="Kids Zone"
              secondary="For information about the Kids Zone area, please contact us at 601-508-0170 or contact@ginghamtree.com."
            />
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            mb: 4,
            border: `1px solid ${brand.hairline}`,
            backgroundColor: '#FFF6E6',
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            Good to know
          </Typography>
          <List sx={{ p: 0 }}>
            <Row
              icon={<InfoIcon />}
              primary="What to Expect"
              secondary="All items at the festival are handmade by local artisans. You'll find unique crafts, art, and homemade goods like jellies, jams, pickles, and honey."
            />
            <Row
              icon={<DirectionsCarIcon />}
              primary="Parking"
              secondary="We have organized parking to be as efficient as possible for the flow of traffic. Please follow the directions of our parking attendants."
            />
          </List>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: `1px solid ${brand.hairline}`,
            backgroundColor: brand.paper,
          }}
        >
          <Typography variant="h3" sx={{ color: brand.navy, mb: 2 }}>
            Contact Information
          </Typography>
          <List dense sx={{ p: 0 }}>
            <ContactRow
              icon={<LocationOnIcon />}
              primary="Mailing Address"
              secondary="Gingham Tree, P.O. Box 534, Lucedale, MS 39452"
            />
            <ContactRow primary="Chairperson" secondary="Gina Harris - 601-508-0170" />
            <ContactRow primary="Email" secondary="contact@ginghamtree.com" />
            <ContactRow primary="Website" secondary="www.ginghamtree.com" />
            <ContactRow primary="Food Chairman" secondary="Audra Rouse - 601-947-0761" />
            <ContactRow primary="Kids Zone Area" secondary="601-508-0170 or contact@ginghamtree.com" />
          </List>
        </Paper>
      </Container>
    </MainLayout>
  );
};

const Row = ({ icon, primary, secondary, secondaryNode }) => (
  <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
    <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={primary}
      secondary={secondaryNode || secondary}
      primaryTypographyProps={primaryTextProps}
      secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
    />
  </ListItem>
);

const ContactRow = ({ icon, primary, secondary }) => (
  <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
    {icon && <ListItemIcon sx={{ color: brand.navy, mt: 0.5 }}>{icon}</ListItemIcon>}
    <ListItemText
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{ fontWeight: 600, fontSize: '1.0625rem', color: brand.navy }}
      secondaryTypographyProps={{ fontSize: '1.0625rem', color: brand.textPrimary }}
      sx={!icon ? { pl: '56px' } : undefined}
    />
  </ListItem>
);

export default Visitors;
